/**
 * InitializeVideoUseCase
 * 
 * Business logic for initializing encrypted video playback.
 * 
 * Responsibilities:
 * 1. Call cloud function to get CDN tokens and encrypted keys
 * 2. Decrypt and process encryption keys
 * 3. Build video URL with CDN authentication using MediaUtils
 * 4. Return video decryption entity ready for playback
 * 
 * Matches Flutter video_player_notifier.dart -> initVideo flow
 */

import type { VideoDecryptionEntity, VideoInitParams } from '@/domain/entities/video/videoDecryptionEntity';
import type { UserMediaEntity } from '@/domain/entities/userMedia/userMediaEntity';
import { cloudFunctionsService } from '@/data/services/cloudFunctionsService';
import { videoEncryptionService } from '@/domain/services/video/videoEncryptionService';
import { MediaUtils } from '@/domain/utils/mediaUtils';

export class InitializeVideoUseCase {
  constructor(
    private cloudFunctions = cloudFunctionsService,
    private encryptionService = videoEncryptionService
  ) { }

  /**
   * Initialize video for encrypted playback using UserMediaEntity
   * 
   * @param params - Video initialization parameters
   * @param media - Optional UserMediaEntity used to build the video path
   * @param has4kAccess - Whether the user's `consumerPro` premium package is
   *   active. When true the full-quality (`master.m3u8`) manifest is served;
   *   otherwise the light (`master_light.m3u8`) manifest. Mirrors Flutter
   *   `UserMedia.path` reading `ContentUtils.has4kAccess`
   *   (`lib/data/models/user_media.dart:106-118`). Default `false` is the safe
   *   parity default: a user with no active `consumerPro` package gets light
   *   quality, matching Flutter's `hasPremiumPackage` returning false.
   * @returns Video decryption entity with keys and CDN token
   * @throws Error if initialization fails
   */
  async execute(params: VideoInitParams, media?: UserMediaEntity, has4kAccess = false): Promise<InitializeVideoResult> {
    const { videoId, uid, iat } = params;

    try {
      // 1. Get CDN tokens and encrypted keys from cloud function
      const cdnTokens = await this.cloudFunctions.generateMediaCdnTokens(videoId);

      if (!cdnTokens) {
        throw new Error('Failed to retrieve CDN tokens from cloud function');
      }

      // 2. Process encryption: decrypt and unshift keys
      const decryptionEntity = await this.encryptionService.processVideoEncryption(
        videoId,
        uid,
        iat,
        cdnTokens
      );

      // 3. Build video URL using MediaUtils if media entity is provided
      let videoUrl: string;

      if (media) {
        // Use MediaUtils to build URL with proper video type checking
        const baseUrl = MediaUtils.getMediaUrl(media, has4kAccess);

        if (!baseUrl) {
          throw new Error('Failed to generate media URL from entity');
        }

        // Append CDN token to the URL
        videoUrl = `${baseUrl}?hdnts=${cdnTokens.hdnts}`;
      } else {
        // Fallback: Build URL manually (for cases without UserMediaEntity)
        videoUrl = MediaUtils.buildVideoUrl(videoId, cdnTokens.hdnts, has4kAccess);
      }

      return {
        success: true,
        decryptionEntity,
        videoUrl,
        error: null
      };
    } catch (error) {
      return {
        success: false,
        decryptionEntity: null,
        videoUrl: null,
        error: error instanceof Error ? error : new Error('Unknown error initializing video')
      };
    }
  }

  /**
   * Check if video decryption is already cached and still valid
   */
  isVideoCached(videoId: string): boolean {
    const cached = this.encryptionService.getVideoDecryption(videoId);
    return cached !== null;
  }

  /**
   * Get cached video decryption data
   */
  async getCachedVideo(videoId: string): Promise<VideoDecryptionEntity | null> {
    return await this.encryptionService.getVideoDecryption(videoId);
  }

  /**
   * Clear cached video data
   */
  clearVideoCache(videoId: string): void {
    this.encryptionService.clearVideoDecryption(videoId);
  }
}

/**
 * Result of video initialization
 */
export interface InitializeVideoResult {
  /** Whether initialization succeeded */
  success: boolean;

  /** Decryption entity (null if failed) */
  decryptionEntity: VideoDecryptionEntity | null;

  /** Full video URL with CDN token (null if failed) */
  videoUrl: string | null;

  /** Error if initialization failed */
  error: Error | null;
}

// Export factory function for easy instantiation
export const createInitializeVideoUseCase = () => new InitializeVideoUseCase();
